import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';

import type { Company, CompanyCreateInput, CompanyPartyType, CompanyUpdateInput } from './types';

const roleByParty: Record<CompanyPartyType, string> = {
  supplier: 'source_supplier',
  processor: 'operator',
  platform: 'owner',
};

export interface CompanyListParams {
  active_only?: boolean;
  q?: string;
  party_type?: CompanyPartyType;
  page?: number;
  per_page?: number;
}

export interface CompanyListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface CompanyListResult {
  data: Company[];
  meta: CompanyListMeta;
}

export const listCompaniesWithMeta = async (
  params: CompanyListParams = {},
): Promise<CompanyListResult> => {
  const res = await apiClient.get<ApiSuccess<Company[], CompanyListMeta>>('/companies', {
    params: { active_only: false, page: 1, per_page: 50, ...params },
  });
  const data = res.data.data ?? [];
  return {
    data,
    meta: res.data.meta ?? {
      total: data.length,
      page: params.page ?? 1,
      per_page: params.per_page ?? data.length,
    },
  };
};

export const listCompanies = async (params: CompanyListParams = {}): Promise<Company[]> => {
  const result = await listCompaniesWithMeta({ per_page: 200, ...params });
  return result.data;
};

export const createCompany = async (body: CompanyCreateInput): Promise<Company> => {
  const res = await apiClient.post<ApiSuccess<Company>>('/companies', {
    legal_name: body.legal_name,
    display_name: body.display_name,
    company_role: roleByParty[body.party_type],
    party_type: body.party_type,
    registration_info: body.registration_info,
    short_code: body.short_code,
  });
  return res.data.data;
};

export const updateCompany = async (id: string, body: CompanyUpdateInput): Promise<Company> => {
  const res = await apiClient.put<ApiSuccess<Company>>(`/companies/${id}`, body);
  return res.data.data;
};

export const deactivateCompany = async (id: string): Promise<void> => {
  await apiClient.delete(`/companies/${id}`);
};

export const activateCompany = async (id: string): Promise<Company> => {
  const res = await apiClient.put<ApiSuccess<Company>>(`/companies/${id}`, { active_flag: true });
  return res.data.data;
};
